import { createContext, useContext, useEffect, useState } from 'react';
import AuthContext from '../utils/secure-route/AuthContext';

const ConfigContext = createContext();

export const ConfigProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [config, setConfig] = useState({
    dateFormat: "MM-DD-YYYY",
    currency: "USD",
    storeId: null,
    invoiceLayoutSettings: {},
    storeInfo: {},
  });

   useEffect(() => {
    if (user?.store) {
      // Preserve fields already loaded from /api/config (invoiceLayoutSettings,
      // storeInfo, currency, dateFormat) instead of clobbering them back to
      // hardcoded defaults every time the user object changes.
      setConfig(prev => ({
        ...prev,
        storeId: user.store,
      }));
    }
  }, [user?.store]);

  useEffect(() => {
    if (!config.storeId) {
      const localData = localStorage.getItem("config");
      if (localData) {
        const parsed = JSON.parse(localData);

        if (parsed.storeId) {
          setConfig(parsed);
          return;
        }
      }

      fetch('/api/config')
        .then(res => res.json())
        .then(data => {
          if (data?.store_configuration) {
            const cfg = {
              dateFormat: data.store_configuration.date_format || "MM-DD-YYYY",
              currency: data.store_configuration.currency || "USD",
              storeId: data.store_configuration.store || null,
              invoiceLayoutSettings: data.store_configuration.invoice_layout_settings || {},
              storeInfo: {
                // The /api/config (CheckUserType) response returns these as
                // flat top-level fields, not a nested `store` object.
                name: data?.store_name || "",
                address: data?.store_address || "",
                contact: data?.store_email || "",
                logo: data?.store_logo || "",
              },
            };

            setConfig(cfg);
            localStorage.setItem("config", JSON.stringify(cfg));
          }
        })
        .catch(console.error);
    }
  }, [config.storeId]);  

  useEffect(() => {
    localStorage.setItem("config", JSON.stringify(config));
  }, [config]);

  return (
  <ConfigContext.Provider
      value={{
        config,
        setConfig,
        dateFormat: config.dateFormat,
        currency: config.currency,
        storeId: config.storeId,
        setDateFormat: (df) => setConfig(prev => ({ ...prev, dateFormat: df })),
        setCurrency: (cur) => setConfig(prev => ({ ...prev, currency: cur })),
        setStoreId: (id) => setConfig(prev => ({ ...prev, storeId: id })),
        invoiceLayoutSettings: config.invoiceLayoutSettings || {},
        storeInfo: config.storeInfo || {},
        setInvoiceLayoutSettings: (settings) => setConfig(prev => ({ ...prev, invoiceLayoutSettings: settings })),
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => useContext(ConfigContext);
export default ConfigContext;
